import EditRubricForm from 'components/Rubrics/EditRubricForm';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import rubricService from 'services/rubric';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    Loading,
    ResponsiveDialog,
    RichTextArea,
    Stack,
    styled,
    Tabs,
    Text,
} from 'shell/components';
import { UploadFileForm } from 'shell/components/UploadFileDialog';
import { RUBRIC_ALLOWED_FILE_TYPES, RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getRubric, getWordCount, isNatural, roundCriteriaOptionRates, sortCriteria } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const TABS = {
    FILE: 'file',
    TEXT: 'text',
};

const defaultRubricErrors = {
    title: '',
    criteria: {},
};

const CreateRubricExistingDialog = memo(({ open, onClose, onCreate }) => {
    const { course, setRubrics } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const { device } = useViewport();

    const isCancelled = useRef(false);

    const [tab, setTab] = useState(TABS.FILE);
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [value, setValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [rubricErrors, setRubricErrors] = useState(defaultRubricErrors);
    const [hasClickedSubmitRubric, setHasClickedSubmitRubric] = useState(false);
    const [hasClickedSaveAsTemplate, setHasClickedSaveAsTemplate] = useState(false);

    const step = isLoading ? 2 : result ? 3 : 1;

    const handleClose = () => {
        setDialog({
            open: true,
            message: t('dialog.exit-rubric-creation.title', 'Exit Rubric Creation?'),
            description: t(
                'dialog.exit-rubric-creation.description',
                'If you leave this page before the rubric is saved you will lose all of the details you entered during setup.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                onClose();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickBack = () => {
        setDialog({
            open: true,
            message: t('dialog.return-to-settings.title', 'Returning to Settings Will Interrupt Rubric Generation'),
            description: t(
                'dialog.return-to-settings.description',
                'If you proceed, the rubric will not be generated. Do you want to continue?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setIsLoading(false);
                isCancelled.current = true;
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const validateTitle = useCallback(() => {
        const error = result.title.trim() ? '' : t('error.empty-name', 'Please enter a name');
        setRubricErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [result?.title, t]);

    const validateCriteria = useCallback(() => {
        let error = false;
        result.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isNatural(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setRubricErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? t('error.empty-number', 'Please enter a number') : '',
                                description: isDescriptionInvalid
                                    ? t('error.word-count-exceeded', 'Word count exceeded')
                                    : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [result?.criteria, t]);

    const onClickEditSettings = () => {
        setDialog({
            open: true,
            message: t('dialog.editing-settings-warning.title', 'Editing Settings Will Erase This Rubric'),
            description: t(
                'dialog.editing-settings-warning.description',
                'If you continue, you will need to generate a new rubric.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setResult(null);
                setRubricErrors({ title: '', criteria: {} });
                setHasClickedSubmitRubric(false);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const renderDialogContent = () => {
        if (isLoading) {
            return (
                <LoadingStack device={device}>
                    <Button
                        sx={{ alignSelf: 'flex-start' }}
                        startIcon={<Icon kind={'arrowLeft'} />}
                        variant={'borderless'}
                        onClick={onClickBack}
                    >
                        {t('common:controls.back', 'Back')}
                    </Button>
                    <CenterStack device={device} spacing={20}>
                        <Stack spacing={2}>
                            <Text align={'center'} variant={'h6'} weight={'semibold'}>
                                {t('rubric.create-ai-rubric.loading.title', 'Your rubric is being generated!')}
                            </Text>
                            <Text align={'center'}>
                                {t(
                                    'keep-window-open',
                                    'Please keep this window open until this process completes. This may take a few minutes.'
                                )}
                            </Text>
                        </Stack>
                        <Loading />
                    </CenterStack>
                </LoadingStack>
            );
        }

        if (result) {
            return (
                <ResultStack alignItems={'flex-start'} spacing={5}>
                    <Stack spacing={2}>
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={4}>
                            <Text component={'p'} variant={'h6'} weight={'semibold'}>
                                {t('rubric.create-ai-rubric.review.title', 'Review Rubric')}
                            </Text>
                            {device === 'mobile' && (
                                <Button
                                    startIcon={<Icon kind={'edit'} />}
                                    variant={'secondary'}
                                    onClick={onClickEditSettings}
                                >
                                    {t('rubric.create-ai-rubric.review.edit-button', 'Edit')}
                                </Button>
                            )}
                        </Stack>
                        <Text>
                            {t(
                                'rubric.create-ai-rubric.review.description',
                                'Please review and verify all AI-generated information for accuracy and completeness.'
                            )}
                        </Text>
                    </Stack>
                    {device !== 'mobile' && (
                        <CustomButton
                            startIcon={<Icon kind={'edit'} />}
                            variant={'secondary'}
                            onClick={onClickEditSettings}
                        >
                            {t('rubric.create-ai-rubric.review.edit-button', 'Edit')}
                        </CustomButton>
                    )}
                    <CustomEditRubricForm
                        device={device}
                        form={result}
                        setForm={setResult}
                        errors={rubricErrors}
                        setErrors={setRubricErrors}
                        hasClickedSubmit={hasClickedSubmitRubric}
                        validateTitle={validateTitle}
                        validateCriteria={validateCriteria}
                    />
                </ResultStack>
            );
        }

        return (
            <Stack spacing={5}>
                <Text>
                    {t(
                        'rubric.create-existing-rubric.description',
                        'Select either to upload a file or paste your text to submit your rubric.'
                    )}
                </Text>
                <Tabs
                    aria-label={'choose how to input your existing rubric'}
                    options={[
                        {
                            key: TABS.FILE,
                            value: t('upload-files.upload-file', 'Upload File'),
                            content: (
                                <Box sx={{ pt: 3 }}>
                                    <UploadFileForm
                                        allowedFileTypes={RUBRIC_ALLOWED_FILE_TYPES}
                                        max={1}
                                        files={files}
                                        setFiles={setFiles}
                                        error={error}
                                        setError={setError}
                                    />
                                </Box>
                            ),
                        },
                        {
                            key: TABS.TEXT,
                            value: t('rubric.create-existing-rubric.text.title', 'Paste Text'),
                            content: (
                                <Stack sx={{ pt: 3 }} spacing={4}>
                                    <Text color={'light'}>
                                        {t(
                                            'rubric.create-existing-rubric.text.description',
                                            'To make sure TimelyGrader effectively reflects your rubric, please provide a description for each criterion, scores for all rating levels, and detailed explanations for each rating level.'
                                        )}
                                    </Text>
                                    <CustomRichTextArea
                                        placeholder={t(
                                            'rubric.create-existing-rubric.text.input-placeholder',
                                            'Paste your rubric here, include criteria, points, and level descriptions for the best results.'
                                        )}
                                        value={value}
                                        onChange={(html) => setValue(html)}
                                    />
                                </Stack>
                            ),
                        },
                    ]}
                    selected={tab}
                    onChange={setTab}
                />
            </Stack>
        );
    };

    const onClickGenerate = useCallback(async () => {
        if (tab === TABS.FILE && (!files.length || Boolean(error))) {
            return message.error(t('rubric.create-existing-rubric.file.error', 'Please upload a file.'));
        }
        if (tab === TABS.TEXT && !Boolean(value)) {
            return message.error(t('rubric.create-existing-rubric.text.error', 'Please enter your rubric.'));
        }
        isCancelled.current = false;
        setIsLoading(true);
        const rubricSource = {
            [TABS.FILE]: {
                ...files?.[0]?.data,
            },
            [TABS.TEXT]: value,
        }[tab];
        try {
            const response = await getRubric({ type: tab, rubricSource, language: course?.language });
            const data = await response.json();
            if (isCancelled.current) return;
            const { result } = data;
            if (!result || !result.criteria.length || result.criteria.some(({ options }) => options?.length < 2)) {
                throw new Error(JSON.stringify(result));
            }
            setResult({
                title: result.title,
                criteria: result.criteria.map((criterion) => {
                    return {
                        ...criterion,
                        id: uuid(),
                        options: criterion.options
                            .sort((a, b) => {
                                const numA = Number(a.rate);
                                const numB = Number(b.rate);
                                if (a.rate === '') return 1;
                                if (b.rate === '') return -1;
                                if (!numA && !numB) return 0;
                                return numB - numA;
                            })
                            .map((option) => ({ ...option, id: uuid(), rate: option.rate.toString() })),
                    };
                }),
                sort: 'desc',
            });
        } catch (e) {
            console.error(e);
            message.error(
                t(
                    'rubric.create-ai-rubric.error.rubric-generation-failed',
                    'Rubric generation failed, please try again.'
                )
            );
        } finally {
            setIsLoading(false);
        }
    }, [course?.language, error, files, message, t, tab, value]);

    const onClickSaveAsTemplate = useCallback(() => {
        setHasClickedSubmitRubric(true);
        const isValid = validateTitle() && validateCriteria();
        if (isValid) {
            setDialog({
                open: true,
                message: t(
                    'rubric.create-ai-rubric.save-rubric-warning.title',
                    'Did You Verify All AI-Generated Information?'
                ),
                description: t(
                    'rubric.create-ai-rubric.save-rubric-warning.description',
                    'Before proceeding, please confirm that you have reviewed and verified all AI-generated information for accuracy and completeness.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.confirm', 'Confirm'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    const timestamp = new Date().getTime();
                    const criteriaRoundedOptionRates = roundCriteriaOptionRates(result.criteria);
                    const sortedCriteria = sortCriteria(criteriaRoundedOptionRates, result.sort);
                    const rubric = {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        rubric: { ...result, criteria: sortedCriteria },
                        user_id: auth.user_id,
                    };
                    setHasClickedSaveAsTemplate(true);
                    setRubrics((current) => [...current, rubric]);
                    rubricService.createRubric(rubric);
                    message.success(t('message.rubric-saved-success', 'Rubric was saved successfully.'));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    }, [auth.user_id, closeDialog, message, result, setDialog, setRubrics, validateCriteria, validateTitle, t]);

    const onClickCreateRubric = useCallback(() => {
        setHasClickedSubmitRubric(true);
        const hasAtLeastOneCriterion = result.criteria.length > 0;
        const isValid = validateTitle() && validateCriteria() && hasAtLeastOneCriterion;
        if (isValid) {
            setDialog({
                open: true,
                message: t('dialog.save-rubric-warning.title', 'Did You Verify All AI-Generated Information?'),
                description: t(
                    'dialog.save-rubric-warning.description',
                    'Before proceeding, please confirm that you have reviewed and verified all AI-generated information for accuracy and completeness.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.confirm', 'Confirm'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    const criteriaRoundedOptionRates = roundCriteriaOptionRates(result.criteria);
                    const sortedCriteria = sortCriteria(criteriaRoundedOptionRates, result.sort);
                    onCreate({ ...result, criteria: sortedCriteria });
                    onClose();
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            message.error(t('error.invalid-rubric', 'The rubric is not valid.'));
        }
    }, [closeDialog, message, onClose, onCreate, result, setDialog, t, validateCriteria, validateTitle]);

    const renderDialogActions = () => {
        if (isLoading) {
            if (device === 'mobile') return null;
            return <Button disabled>{t('rubric.process-rubric', 'Process Rubric')}</Button>;
        }

        if (result) {
            return (
                <Stack direction={'row'} spacing={4}>
                    <Button disabled={hasClickedSaveAsTemplate} variant={'secondary'} onClick={onClickSaveAsTemplate}>
                        {t('common:controls.save-as-template', 'Save as a Template')}
                    </Button>
                    <Button onClick={onClickCreateRubric}>{t('rubric.create-rubric', 'Create Rubric')}</Button>
                </Stack>
            );
        }

        return (
            <Button
                disabled={tab === TABS.FILE && Boolean(files.length) && files.every(({ loading }) => loading)}
                onClick={onClickGenerate}
            >
                {t('rubric.process-rubric', 'Process Rubric')}
            </Button>
        );
    };

    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                isCancelled.current = false;
                setTab(TABS.FILE);
                setFiles([]);
                setError('');
                setValue('');
                setIsLoading(false);
                setResult(null);
                setRubricErrors(defaultRubricErrors);
                setHasClickedSubmitRubric(false);
                setHasClickedSaveAsTemplate(false);
            }, 150);
        }
    }, [open]);

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} step={step} onClose={handleClose}>
            {!(device === 'mobile' && (isLoading || result)) && (
                <CustomDialogTitle device={device}>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                            {t('rubric.create-existing-rubric.title', 'Submit Your Rubric')}
                        </Text>
                        <IconButton
                            aria-label={'close'}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleClose}
                        />
                    </Stack>
                </CustomDialogTitle>
            )}
            {device !== 'mobile' && <Divider />}
            <CustomDialogContent device={device}>{renderDialogContent()}</CustomDialogContent>
            {device !== 'mobile' && <Divider />}
            <CustomDialogActions device={device}>{renderDialogActions()}</CustomDialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(({ step }) => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: step === 1 ? '750px' : '100%',
            height: step === 1 ? '650px' : '100%',
        },
    };
});

const CustomDialogTitle = styled(DialogTitle)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomDialogContent = styled(DialogContent)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomDialogActions = styled(DialogActions)(({ device, theme }) => {
    return {
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const LoadingStack = styled(Stack)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            position: 'relative',
            width: '100%',
            height: '100%',
        }),
    };
});

const CenterStack = styled(Stack)(({ device }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        ...(device === 'mobile' && {
            width: '80%',
        }),
    };
});

const ResultStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomButton = styled(Button)(() => {
    return {
        zIndex: 1,
        alignSelf: 'flex-end',
    };
});

const CustomEditRubricForm = styled(EditRubricForm)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            position: 'relative',
            top: '-56px',
        }),
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        '& > .MuiRichTextArea-root': {
            minHeight: '21rem',
            maxHeight: '21rem',
        },
    };
});

export default CreateRubricExistingDialog;
